import styled from 'styled-components';
import {
  TranslatedItem,
  useTranslateItem,
} from 'src/hooks/directus/useTranslateItem';
import { PimImage } from 'src/components/common/PimImage';
import { BaseProductDTO, ProductCategoryCode } from 'src/models/pim';
import { route } from 'src/services/route';
import { TrackedLink } from 'src/components/TrackedLink';
import { Button } from 'src/components/common/Button';
import { useTranslate } from 'react-t7e';
import { Paragraph } from 'src/components/common/typography/Text';
import { media } from 'src/styles/designs';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { useMemo } from 'react';

export type ProductPageInvolvedProductsItemProps =
  TranslatedItem<BaseProductDTO> & { description?: string };

const Container = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 40px 24px;
  text-align: center;

  width: 280px;

  background: ${({ theme }) => theme.palette.background};
  border: 2px solid transparent;
  transition: 0.35s;

  &:hover {
    border-color: ${({ theme }) => theme.buttons.default.primary.hover.accent};
  }

  ${media.w.greaterThanOrEqual.desktop} {
    width: 337px;
  }
`;

const Name = styled.h5`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  margin-bottom: 8px;
  margin-top: auto;

  color: ${({ theme }) => theme.text.palette.primary};
`;

const ImageContainer = styled.div`
  position: relative;
  width: 220px;
  height: 220px;
  object-fit: contain;
`;

const StyledButton = styled(Button)`
  min-width: 195px !important;
  margin-top: auto;
`;

export const ProductPageInvolvedProductsItem = (
  _item: ProductPageInvolvedProductsItemProps,
) => {
  const { _ } = useTranslate();
  const item = useTranslateItem(_item);

  const descriptionToDisplay = item.description ?? item.description_short;

  const link = useMemo(() => {
    const category = Object.values(ProductCategoryCode).find((code) =>
      item.code.includes(`sct-${code}`),
    );

    if (category) {
      return `/products?${category}`;
    }

    return route.products().page(item);
  }, [item]);

  if (!link) {
    return null;
  }

  return (
    <TrackedLink href={link} passHref>
      <Container>
        <ImageContainer>
          {item.image && (
            <PimImage
              src={item.image.id}
              width={220}
              height={220}
              objectFit="contain"
              sizes="100vw"
            />
          )}
        </ImageContainer>

        <div>
          <Name>{item.name}</Name>
          <Paragraph>
            {descriptionToDisplay && (
              <Markdown>{descriptionToDisplay}</Markdown>
            )}
          </Paragraph>
        </div>

        <StyledButton>{_('Discover More')}</StyledButton>
      </Container>
    </TrackedLink>
  );
};
